import React, { useEffect, useState, useRef } from 'react';
import {
    TextField,
    Callout,
    FontWeights,
    Text,
    DirectionalHint,
    DefaultButton,
    PrimaryButton,
    TooltipHost,
    makeStyles,
    useTheme,
    Stack,
} from '@fluentui/react';
import { FormatValue, GetNumberSep, Format2DecimalValue, MathToFixed, getEmptyGuid, IsWeekDay } from './Utils';
import { useId } from '@fluentui/react-hooks';
import { constants } from '../Styles/constants';
import { PeriodDay } from '../types/PeriodDay';
import { TimeGridMode } from '../types/TimeGridMode';
import { TimeEntry, TimeRegistrationForDate, UserRegistrationPeriod } from '../api/generated/data-contracts';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';
import { useUpdateFromTotalCell } from '../hooks/queries/useUpdateFromTotalCell';
import { useGridContext } from './Contexts/GridContext/context';

interface TotalCellProps {
    initialValue: number;
    periodDays: PeriodDay[];
    timeRow: UserRegistrationPeriod;
    readonly: boolean;
    updated: (
        time: UserRegistrationPeriod,
        day: PeriodDay,
        te: TimeEntry,
        value: number,
        comment: string,
        flagged: boolean,
        mode: TimeGridMode,
        previousValue: number,
        saveSuccess: Function,
        skipApiCall?: boolean,
    ) => void;
    recalculateActualWork?: (assignmentId: string) => void;
    timeLowerLimitConfig: string;
}

function TotalCell(props: TotalCellProps) {
    const { t } = useTranslation('timesheet');
    const theme = useTheme();
    const styles = getStyles();
    const textFieldRef = useRef<any>();
    const targetId = useId();

    const { selectedTimePeriod } = useGridContext();

    // const { onCellUpdated } = useCellCtx();

    const [celltext, setCelltext] = useState('');
    const [cellnumber, setCellnumber] = useState<number>(0);

    const [cellIsError, setCellIsError] = useState<string>('');

    const [cellnumberlastsaved, setCellnumberlastsaved] = useState<number>(0);
    const [textinfocus, setTextinfocus] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);

    const [anyChanges, setAnyChanges] = useState<boolean>(false);
    const [dataSaved, setDataSaved] = useState<boolean>(false);

    const [showTotalChangedCallOut, setShowTotalChangedCallOut] = useState<boolean>(false);
    const [callOutShownCellNumber, setCallOutShownCellNumber] = useState<number>(0);

    const { mutateTotalCell } = useUpdateFromTotalCell();

    useEffect(() => {
        setCelltext(FormatValue(props.initialValue));
        setCellnumber(props.initialValue);
        setCellnumberlastsaved(props.initialValue);

        // Extras to change for totalcell
        setCellIsError('');
        setTextinfocus(false);

        setLoaded(true);
    }, [props.initialValue]);

    useEffect(() => {
        // debugger;
        if (textinfocus === false && loaded === true) {
            if (cellnumber !== cellnumberlastsaved) {
                if (cellIsError === '') {
                    saveData(cellnumber, cellnumberlastsaved);
                    setAnyChanges(false);
                    setTimeout(() => {
                        setDataSaved(true);
                    }, 150);
                    setTimeout(() => {}, 500);
                }
            }
        }
    }, [textinfocus]);

    const onCellUpdatedInternal = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
        setAnyChanges(true);

        isValid(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
        setCelltext(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
        setCellnumber(MathToFixed(parseNumber(text.replace(/\,/g, '.')), 2));
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (cellIsError === '') {
            if (cellnumber !== cellnumberlastsaved) {
                setCallOutShownCellNumber(cellnumber);
                setShowTotalChangedCallOut(true);
                return;
            }
        }
        // Set TextInFocus false, as it won't open dialog for change
        setTextinfocus(false);
    };

    const isValid = (value: string): void => {
        const newVal = value.replace(/,/g, '.');

        if (isValidNumber(newVal)) {
            const newValNum = parseFloat(newVal);

            // Max hours on period
            let weekDays = props.periodDays.filter(p => IsWeekDay(p.date));
            if (weekDays.length === 0) {
                weekDays = props.periodDays;
            }
            const periodMaxHours = weekDays.length * 24;
            const periodMaxMessage = t('cell.error.periodMaxHours', { value: periodMaxHours });
            const negativeMessage = t('cell.error.negative');
            const biggerMessage = t('cell.error.bigger', { value: Format2DecimalValue(props.timeLowerLimitConfig) });

            if (newValNum > periodMaxHours) {
                setCellIsError(periodMaxMessage);
                return;
            }
            if (newValNum < 0) {
                setCellIsError(negativeMessage);
                return;
            }
            if (props.timeLowerLimitConfig !== '' && newValNum !== 0 && !isNaN(newValNum)) {
                if (parseFloat(newVal) < parseFloat(props.timeLowerLimitConfig)) {
                    setCellIsError(biggerMessage);
                    return;
                }
            }
            setCellIsError('');
            return;
        } else {
            const invalidMessage = t('cell.error.invalid');
            setCellIsError(invalidMessage);
            return;
            // return " ";
        }
    };

    const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setTextinfocus(true);
        if (textFieldRef.current) {
            textFieldRef.current.select();
        }
    };

    const onYesClick = (): void => {
        setShowTotalChangedCallOut(false);
        setCellnumber(callOutShownCellNumber);
        setCelltext(FormatValue(callOutShownCellNumber));

        setTimeout(() => {
            setTextinfocus(false);
        }, 300);
    };

    const onNoClick = (): void => {
        setShowTotalChangedCallOut(false);
        setCellnumber(cellnumberlastsaved);
        setCelltext(FormatValue(cellnumberlastsaved));

        setTimeout(() => {
            setTextinfocus(false);
        }, 300);
    };

    const {
        initialData: {
            configuration: { weekConfig },
            calendarHolidays,
        },
    } = useConfigValuesState();

    const saveData = (hours: number, previousHours: number): void => {
        const holidaysAsIso = calendarHolidays.map(m => new Date(m).getISOStringMidnight());
        const enabledWorkdays = weekConfig.filter(w => w.enabled).map(w => w.name.toLowerCase());
        let weekDays = props.periodDays.filter(
            p => enabledWorkdays.includes(p.originalName.toLowerCase()) && !holidaysAsIso.includes(p.date.getISOStringMidnight()),
        );
        let nonWeekDays = props.periodDays.filter(
            p => !enabledWorkdays.includes(p.originalName.toLowerCase()) || holidaysAsIso.includes(p.date.getISOStringMidnight()),
        );
        console.log('TotalCellChange should be propagated and saved', hours, previousHours);

        if (weekDays.length === 0) {
            weekDays = props.periodDays;
            nonWeekDays = [];
        }

        const rounded = MathToFixed(Number(cellnumber / weekDays.length), 2);

        const registrations: TimeRegistrationForDate[] = [];

        weekDays.forEach(async periodDay => {
            const selectedTimeEntry = props.timeRow.timeEntries?.filter(te => new Date(te.date).getTime() === periodDay.date.getTime())[0];

            registrations.push({
                taskId: props.timeRow.taskId,
                workTypeId: props.timeRow.workType?.id ?? getEmptyGuid(),
                hours: rounded,
                comment: undefined,
                flagged: selectedTimeEntry?.flagged,
                userReportingPeriodId: selectedTimePeriod.reportPeriodId,
                date: periodDay.date.getISOStringMidnight(),
            });
        });
        nonWeekDays.forEach(async periodDay => {
            const selectedTimeEntry = props.timeRow.timeEntries?.filter(te => new Date(te.date).getTime() === periodDay.date.getTime())[0];

            registrations.push({
                taskId: props.timeRow.taskId,
                workTypeId: props.timeRow.workType?.id ?? getEmptyGuid(),
                hours: 0,
                comment: undefined,
                flagged: selectedTimeEntry?.flagged,
                userReportingPeriodId: selectedTimePeriod.reportPeriodId,
                date: periodDay.date.getISOStringMidnight(),
            });
        });

        mutateTotalCell({
            date: props.periodDays[0].date.getISOStringMidnight(),
            timeRegistrations: registrations,
            setDataSaved,
            onSuccess: () => {
                // Notify parent components to update their local state by calling updated for each day
                weekDays.forEach(periodDay => {
                    const selectedTimeEntry = props.timeRow.timeEntries?.filter(te => new Date(te.date).getTime() === periodDay.date.getTime())[0];
                    const timeEntry: TimeEntry = selectedTimeEntry || {
                        id: getEmptyGuid(),
                        date: periodDay.date.getISOStringMidnight(),
                        hours: 0,
                        comment: '',
                        flagged: false,
                    };

                    console.log(rounded);
                    // Call updated callback to notify parent components of the change
                    props.updated(
                        props.timeRow,
                        periodDay,
                        timeEntry,
                        rounded,
                        '',
                        selectedTimeEntry?.flagged || false,
                        TimeGridMode.MyTime,
                        selectedTimeEntry?.hours || 0,
                        () => {},
                        true, // skipApiCall - API call already made by mutateTotalCell
                    );
                });

                // Also handle non-work days (set to 0 hours)
                nonWeekDays.forEach(periodDay => {
                    const selectedTimeEntry = props.timeRow.timeEntries?.filter(te => new Date(te.date).getTime() === periodDay.date.getTime())[0];
                    const timeEntry: TimeEntry = selectedTimeEntry || {
                        id: getEmptyGuid(),
                        date: periodDay.date.getISOStringMidnight(),
                        hours: 0,
                        comment: '',
                        flagged: false,
                    };

                    // Call updated callback to set non-work days to 0 hours
                    props.updated(
                        props.timeRow,
                        periodDay,
                        timeEntry,
                        0,
                        '',
                        selectedTimeEntry?.flagged || false,
                        TimeGridMode.MyTime,
                        selectedTimeEntry?.hours || 0,
                        () => {},
                        true, // skipApiCall - API call already made by mutateTotalCell
                    );
                });

                // Fix race condition: Recalculate actualWork from sum of time entries
                // Use setTimeout to ensure all state updates are complete before recalculating
                setTimeout(() => {
                    if (props.recalculateActualWork) {
                        props.recalculateActualWork(props.timeRow.assignmentId);
                    }
                }, 0);
            },
        });
    };

    const onKeyDown = (event: any): void => {
        if (event.keyCode === 13) {
            // Enter key
            if (cellIsError === '') {
                if (cellnumber !== cellnumberlastsaved) {
                    setCallOutShownCellNumber(cellnumber);
                    setShowTotalChangedCallOut(true);
                    return;
                }
            }
            // Set TextInFocus false, as it won't open dialog for change
            setTextinfocus(false);
        }
    };

    return (
        <div className={styles.root}>
            <TooltipHost content={cellIsError} closeDelay={250}>
                <TextField
                    id={targetId}
                    componentRef={textFieldRef}
                    className="cellInput"
                    value={celltext}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    onChange={onCellUpdatedInternal}
                    disabled={props.readonly}
                    autoComplete="off"
                    style={{ textAlign: 'center' }}
                    styles={{
                        root: {
                            // margin: "0 auto",
                            // maxWidth: constants.cellInputMaxWidth,
                        },
                        fieldGroup: {
                            borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,0.75)' : 'rgba(0,0,0,0)'}`,
                            backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                            borderWidth: '2px',
                            '&:hover': {
                                borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,1)!important' : 'rgba(0,0,0,0)'}`,
                                backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)' : 'rgba(0,0,0,0)'}`,
                            },
                            '&::after': {
                                borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,1)' : 'rgba(96,97,170,1)'}`,
                            },
                        },
                        field: {
                            textAlign: 'right',
                            paddingRight: theme.spacing.s1,
                            paddingLeft: 7,
                        },
                    }}
                />
            </TooltipHost>
            {showTotalChangedCallOut && (
                <Callout
                    target={`#${targetId}`}
                    className={styles.callout}
                    ariaLabelledBy="{labelId}"
                    ariaDescribedBy="{descriptionId}"
                    role="alertdialog"
                    gapSpace={0}
                    directionalHint={DirectionalHint.leftBottomEdge}
                    setInitialFocus
                    dismissOnTargetClick={false}
                >
                    <Stack tokens={{ childrenGap: 20 }}>
                        <div className={styles.header}>
                            <Text className={styles.title}>{t('dialog.spread.title', { value: callOutShownCellNumber })}</Text>
                        </div>
                        <div className={styles.inner} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <PrimaryButton text={t('dialog.spread.confirm')} onClick={onYesClick} className="calloutYesBtn" />
                            <DefaultButton text={t('dialog.spread.cancel')} onClick={onNoClick} className="calloutNoBtn" />
                        </div>
                    </Stack>
                </Callout>
            )}
        </div>
    );
}

export default TotalCell;

const isValidNumber = (num: string) => {
    if (num === '') {
        return true;
    }
    if (isNaN(parseFloat(num))) {
        return false;
    }
    return true;
};

const parseNumber = (num: string) => {
    if (num === '') {
        return 0;
    }
    if (isNaN(parseFloat(num))) {
        return 0;
    }
    return parseFloat(num);
};

const getStyles = makeStyles(theme => ({
    root: [
        'cellInner',
        {
            position: 'relative',
            height: '100%',
            width: '100%',
            maxWidth: constants.cellInputMaxWidth,
            margin: '0 auto',
            display: 'flex',
        },
    ],
    buttonArea: {
        verticalAlign: 'top',
        display: 'inline-block',
        textAlign: 'center',
        margin: '0 100px',
        minWidth: 130,
        height: 32,
    },
    callout: {
        maxWidth: 800,
        minWidth: 300,
    },
    header: {
        padding: '30px 40px 12px',
        color: theme.semanticColors.bodyText,
    },
    title: [
        theme.fonts.mediumPlus,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    inner: {
        height: '100%',
        padding: '0 24px 20px',
    },
    actions: {
        position: 'relative',
        marginTop: 20,
        width: '100%',
        whiteSpace: 'nowrap',
    },
    subtext: [
        theme.fonts.small,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    link: [
        theme.fonts.medium,
        {
            color: theme.palette.neutralPrimary,
        },
    ],
}));
